import { useQuery } from '@tanstack/react-query';
import { platformApi } from '@/lib/platformApi';
import { cn } from '@/lib/utils';

/**
 * TenantSelect — the one place the Platform Console picks an organisation.
 * A dark, console-styled <select> fed by a shared tenants query, so operators
 * never paste a raw chambers UUID. Reused by Billing, Audit, Feature Flags, etc.
 */

interface TenantOption {
  id: string;
  name: string;
  slug?: string;
}

/** Shared tenants list — cached so every picker dedupes to one fetch. */
export function useTenants() {
  return useQuery<TenantOption[]>({
    queryKey: ['platform', 'tenants', 'all'],
    queryFn: async () => {
      const res = await platformApi.get('/tenants', { params: { limit: 100 } });
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      return list as TenantOption[];
    },
    staleTime: 60_000,
  });
}

export function TenantSelect({
  value,
  onChange,
  includeAll = false,
  allLabel = 'All organisations',
  placeholder = 'Select organisation…',
  disabled,
  className,
}: {
  value: string;
  onChange: (id: string) => void;
  /** Add a leading empty option (value '') for use as a filter. */
  includeAll?: boolean;
  allLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const { data: tenants = [], isLoading } = useTenants();

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || isLoading}
      className={cn(
        'h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-neutral-text focus:border-plat-focus focus:outline-none disabled:cursor-not-allowed disabled:text-plat-disabled',
        className,
      )}
    >
      {includeAll ? (
        <option value="">{allLabel}</option>
      ) : (
        <option value="" disabled>
          {isLoading ? 'Loading organisations…' : placeholder}
        </option>
      )}
      {tenants.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
}
